import { useState } from 'react';

const MAX_PIN_LENGTH = 8;
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

interface PinPadProps {
  /** Called with the entered PIN when the operator confirms. */
  onSubmit: (pin: string) => void;
  /** Disable input while a submit is settling. */
  disabled?: boolean;
}

/**
 * Big touch-friendly PIN pad (the WorkLogix kiosk pad, same kiosk.css
 * vocabulary). The PIN is held in component state only long enough to submit,
 * then cleared; it is verified server-side only.
 */
export function PinPad({ onSubmit, disabled = false }: PinPadProps): JSX.Element {
  const [pin, setPin] = useState('');

  const append = (digit: string): void => {
    setPin((current) => (current.length >= MAX_PIN_LENGTH ? current : current + digit));
  };

  const clear = (): void => {
    setPin('');
  };

  const submit = (): void => {
    if (pin.length === 0 || disabled) {
      return;
    }
    onSubmit(pin);
    setPin('');
  };

  return (
    <div className="pinpad">
      <div className="pinpad__display" aria-label="PIN entry">
        {'•'.repeat(pin.length)}
      </div>
      <div className="pinpad__grid">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className="pinpad__key"
            onClick={() => {
              append(key);
            }}
            disabled={disabled}
          >
            {key}
          </button>
        ))}
        <button
          type="button"
          className="pinpad__key pinpad__key--clear"
          onClick={clear}
          disabled={disabled}
        >
          Clear
        </button>
        <button
          type="button"
          className="pinpad__key"
          onClick={() => {
            append('0');
          }}
          disabled={disabled}
        >
          0
        </button>
        <button
          type="button"
          className="pinpad__key pinpad__key--enter"
          onClick={submit}
          disabled={disabled || pin.length === 0}
        >
          Enter
        </button>
      </div>
    </div>
  );
}
